import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Container, Form, FormGroup, Input, InputGroup, InputGroupText, Label, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "react-feather";
import Swal from "sweetalert2";
import { AesDecrypt, AesEncrypt, keyPromise } from "../../Service/crypto";
import webservice from "../../Service/webservice";

// Context handed over by Login.tsx after a temporary-password sign-in. It lives in
// sessionStorage so a refresh keeps the user on this screen; the backend still enforces
// the restriction (no JWT is issued until the password has been changed).
export const PWD_CHANGE_CTX_KEY = "pwd_change_ctx";

interface PwdChangeCtx {
  id: string;
  emailId: string;
  UserName: string;
  password_change_token: string;
}

type FieldErrors = { currentPassword?: string; newPassword?: string; confirmPassword?: string };

const rules = [
  { label: "At least 8 characters", test: (p: string) => p.length >= 8 },
  { label: "At least one uppercase letter (A-Z)", test: (p: string) => /[A-Z]/.test(p) },
  { label: "At least one lowercase letter (a-z)", test: (p: string) => /[a-z]/.test(p) },
  { label: "At least one special character", test: (p: string) => /[^A-Za-z0-9]/.test(p) },
];

const readCtx = (): PwdChangeCtx | null => {
  try {
    const raw = sessionStorage.getItem(PWD_CHANGE_CTX_KEY);
    return raw ? (JSON.parse(raw) as PwdChangeCtx) : null;
  } catch {
    return null;
  }
};

const ForcedChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const ctx = useMemo(readCtx, []);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // No pending password change in this tab -> back to the sign-in page.
  useEffect(() => {
    if (!ctx) navigate("/login", { replace: true });
  }, [ctx, navigate]);

  const backToLogin = () => {
    sessionStorage.removeItem(PWD_CHANGE_CTX_KEY);
    navigate("/login", { replace: true });
  };

  const validateLocally = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!currentPassword) e.currentPassword = "Please enter your current (temporary) password.";
    const failed = rules.find((r) => !r.test(newPassword));
    if (!newPassword) e.newPassword = "Please enter a new password.";
    else if (failed) e.newPassword = `Password rule not met: ${failed.label.toLowerCase()}.`;
    else if (newPassword === currentPassword) e.newPassword = "New password must be different from the temporary password.";
    if (!confirmPassword) e.confirmPassword = "Please confirm the new password.";
    else if (confirmPassword !== newPassword) e.confirmPassword = "New password and confirm password must match.";
    return e;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!ctx || submitting) return;
    setFormError("");
    const local = validateLocally();
    setErrors(local);
    if (Object.keys(local).length) return;

    setSubmitting(true);
    try {
      const key = await keyPromise;
      const EncParams = await AesEncrypt(key, {
        userId: ctx.id,
        passwordChangeToken: ctx.password_change_token,
        currentPassword,
        newPassword,
        confirmPassword,
      });
      const response = await fetch(webservice + "api/Authentication/forcedChangePassword", {
        method: "POST",
        body: JSON.stringify({ EncParams }),
        headers: { "Content-type": "application/json; charset=UTF-8" },
      });
      const res = await AesDecrypt(key, await response.json());

      if (res?.success) {
        sessionStorage.removeItem(PWD_CHANGE_CTX_KEY);
        await Swal.fire({
          title: "Password changed",
          text: "Your password has been updated. Please sign in with your new password to set up multi-factor authentication.",
          icon: "success",
          allowOutsideClick: false,
        });
        navigate("/login", { replace: true });
      } else if (res?.sessionExpired) {
        await Swal.fire({
          title: "Session expired",
          text: res.message || "Your session has expired. Please sign in again.",
          icon: "warning",
          allowOutsideClick: false,
        });
        backToLogin();
      } else {
        setErrors(res?.errors || {});
        setFormError(res?.errors ? "" : res?.message || "Unable to change the password. Please try again.");
      }
    } catch (err) {
      console.error(err);
      setFormError("Unable to reach the server. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!ctx) return null;

  const passwordInput = (
    id: string,
    value: string,
    setValue: (v: string) => void,
    shown: boolean,
    toggle: () => void,
    error?: string
  ) => (
    <>
      <InputGroup>
        <Input
          id={id}
          type={shown ? "text" : "password"}
          value={value}
          invalid={!!error}
          autoComplete={id === "currentPassword" ? "current-password" : "new-password"}
          onChange={(e) => setValue(e.target.value)}
        />
        <InputGroupText className="cursor-pointer" onClick={toggle}>
          {shown ? <EyeOff size={14} /> : <Eye size={14} />}
        </InputGroupText>
      </InputGroup>
      {error && <div className="text-danger">{error}</div>}
    </>
  );

  return (
    <Container fluid className="p-0">
      <Row className="m-0">
        <Col xs={12} className="p-0">
          <div className="login-card login-dark">
            <div>
              <div className="login-main">
                <Form className="theme-form" onSubmit={onSubmit} noValidate>
                  <h3>Change Your Password</h3>
                  <p>
                    Signed in as <b>{ctx.UserName || ctx.emailId}</b>. You must replace your temporary password before continuing.
                  </p>
                  {formError && <Alert color="danger">{formError}</Alert>}
                  <FormGroup>
                    <Label className="col-form-label" for="currentPassword">Current Password / Temporary Password</Label>
                    {passwordInput("currentPassword", currentPassword, setCurrentPassword, showCurrent, () => setShowCurrent(!showCurrent), errors.currentPassword)}
                  </FormGroup>
                  <FormGroup>
                    <Label className="col-form-label" for="newPassword">New Password</Label>
                    {passwordInput("newPassword", newPassword, setNewPassword, showNew, () => setShowNew(!showNew), errors.newPassword)}
                    <ul className="list-unstyled mt-2 mb-0 small">
                      {rules.map((r) => (
                        <li key={r.label} className={r.test(newPassword) ? "text-success" : "text-muted"}>
                          {r.test(newPassword) ? "✓" : "•"} {r.label}
                        </li>
                      ))}
                    </ul>
                  </FormGroup>
                  <FormGroup>
                    <Label className="col-form-label" for="confirmPassword">Confirm New Password</Label>
                    {passwordInput("confirmPassword", confirmPassword, setConfirmPassword, showConfirm, () => setShowConfirm(!showConfirm), errors.confirmPassword)}
                  </FormGroup>
                  <div className="text-end mt-3">
                    <Button color="primary" className="btn-block w-100" type="submit" disabled={submitting}>
                      {submitting ? "Saving..." : "Change Password"}
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default ForcedChangePassword;
